import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import AsStandInLabel from '@/components/UI/AsStandInLabel/AsStandInLabel';
import CloseButton from '@/components/UI/CloseButton/CloseButton';
import { CR_PATH, LONG_FULL_DATE_FORMAT, SUB_CR_PATH, actionTakenSubCampaign, errorTypes } from '@/helpers/constants';
import { getFullFormattedDate } from '@/helpers/utils';
import useApiRequest from '@/hooks/useApiRequest';
import useGlobalSpinnerContext from '@/hooks/useGlobalSpinner';
import useGoTo from '@/hooks/useGoTo';
import useToastContext from '@/hooks/useToastContext';
import { setCampaignHistory, toggleCampaignHistory } from '@/redux/Campaign/campaign.action';
import { getCampaignHistory, getCampaignInfo } from '@/redux/Campaign/campaign.reducer';
import { getSubCampaignInfo } from '@/redux/SubCampaign/subCampaign.reducer';
import { getSettingsInfo } from '@/redux/User/user.reducer';
import campaignServices from '@/services/campaignServices';
import { CampaignHistory as CampaignHistoryType, CampaignInfo, SubCampaignInfo } from '@/types';
import TableListEditableNew, { ALIGN } from '../UI/TableListEditable/TableListEditableNew';
import TooltipComponent from '../UI/Tooltip/TooltipComponent';
import styles from './CampaignHistory.module.scss';

interface CampaignHistoryProps {
  toggleHistory: (show: boolean) => void;
  campaignHistory: CampaignHistoryType;
  setHistory: (history: CampaignHistoryType) => void;
  id: string;
  timeZone: string;
  reloadCR: (fullReset?: boolean, crVersion?: number) => void;
  campaignInfo: CampaignInfo;
  subCampaignInfo: SubCampaignInfo;
  currentTab: string;
  isSubCR?: boolean;
}

interface StatusFromBack {
  action: string;
  date: string;
  department: string;
  last: boolean;
  status: string;
  user: string;
  version: number;
  approvedByStandin: boolean;
}

const getStatusPriority = (status: string) => {
  const priorityMap = {
    IN_CREATION: 1,
    IN_E2_CAUSER_APPROVAL: 2,
    IN_PLANT_VALIDATION: 3,
  };

  return priorityMap[status] || 4;
};

const COLUMN = {
  VERSION: 'version',
  USER: 'user',
  DEPARTMENT: 'department',
  STATUS: 'status',
  ACTION: 'action',
  DATE: 'date',
  ROW_ACTION: 'rowAction',
} as const;

const CampaignHistory: React.FC<CampaignHistoryProps> = (props) => {
  const { toggleHistory, campaignHistory, setHistory, id, timeZone, reloadCR, campaignInfo, subCampaignInfo, currentTab, isSubCR = false } = props;
  const { addToast } = useToastContext();
  const { t } = useTranslation();
  const { goTo } = useGoTo();
  const { setLoading } = useGlobalSpinnerContext();
  const previousTabRef = useRef<string>(currentTab);
  const crVersion = isSubCR ? subCampaignInfo.version : campaignInfo.version;

  const columns = useMemo(() => [
    { name: COLUMN.VERSION, title: t('history.versionTable'), flex: 1, align: ALIGN.CENTER },
    { name: COLUMN.USER, title: t('history.editedByUserCode'), flex: 2, wrapText: true, cellRenderer: (params: { value: unknown; data: Record<string, unknown> }) =>
      params.data.approvedByStandin ? <span><AsStandInLabel marginRight={5} />{params.value as React.ReactNode}</span> : params.value as React.ReactNode,
    },
    { name: COLUMN.DEPARTMENT, title: t('history.editedByUserDepartment'), flex: 1, align: ALIGN.CENTER },
    { name: COLUMN.STATUS, title: t(`history.${isSubCR ? SUB_CR_PATH : CR_PATH}Status`), flex: 2, wrapText: true },
    { name: COLUMN.ACTION, title: t('history.event'), flex: 2, wrapText: true },
    { name: COLUMN.DATE, title: t('history.date'), flex: 2, align: ALIGN.CENTER },
    { name: COLUMN.ROW_ACTION, title: '', flex: 0.5, align: ALIGN.CENTER, cellRenderer: (params: { value: unknown }) => params.value as React.ReactNode },
  ], [t, isSubCR]);

  const redirect = (result: StatusFromBack) => {
    setLoading(true);
    reloadCR(true, result.version);
    goTo(`/${isSubCR ? 'sub-campaign' : 'campaign'}/${id}/${result.version}/description`);
    toggleHistory(false);
    setLoading(false);
  };

  const getUserName = (user: string) => {
    const parts = user.split(',');
    parts.pop();

    return parts.join();
  };

  const getActionLabel = (action: string) => {
    if (isSubCR) {
      if (action === actionTakenSubCampaign.CREATED) return t(`history.SUBCR_CREATED`);
      if (action === actionTakenSubCampaign.REQUESTED_FOR_CHANGES) return t(`history.SUBCR_REQUESTED_FOR_CHANGES`);
    }

    return t(`history.${action}`);
  };

  const getAllResults = (allResults: StatusFromBack[]) => allResults?.map((result) => ({
    version: result.version,
    user: getUserName(result.user || ''),
    approvedByStandin: result.approvedByStandin,
    department: result.department,
    status: t(`campaign.${result.status}`),
    action: getActionLabel(result.action),
    date: getFullFormattedDate(result.date, timeZone, LONG_FULL_DATE_FORMAT),
    rowAction: result.last && (
      crVersion === result.version
        ? (
          <TooltipComponent
            id="tooltip-selected-version"
            title={t('history.selectedVersion') || ''}
          >
            <span>
              <i className={`di icon-pfeil-chevron-rechts ${styles.Redirect}`} />
            </span>
          </TooltipComponent>
        )
        : (
          <span onClick={() => redirect(result)}>
            <i className={`di icon-pfeil-chevron-rechts ${styles.Redirect}`} />
          </span>
        )),

  }));

  const fetchCampaign = useApiRequest(isSubCR ? campaignServices.getSubCampaignHistory : campaignServices.getCampaign);

  const cleanDateString = (dateString: string) => dateString.replace(/:\d{2,} .*/, '');

  useEffect(() => {
    fetchCampaign(id, 'history')
      .then((res) => {
        const sortedStatuses = res.statuses.sort((a, b) => {
          // Clean up date strings before parsing
          const dateA = new Date(cleanDateString(a.date)).getTime();
          const dateB = new Date(cleanDateString(b.date)).getTime();

          if (dateA === dateB) {
            return getStatusPriority(a.status) - getStatusPriority(b.status);
          }

          return dateB - dateA;
        });

        const statuses = getAllResults(sortedStatuses);
        setHistory({ ...campaignHistory, statuses });
      })
      .catch((error) => {
        if (error.name !== errorTypes.ABORT_ERROR) addToast(error.message, 'ERROR');
      });
  }, [timeZone]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (previousTabRef.current === currentTab) return;

    toggleHistory(false);
    previousTabRef.current = currentTab;
  }, [currentTab, toggleHistory]);

  return (
    <section className={styles.Section}>
      <div id="history-title" className={styles.Title}>
        <h3>{t('history.version')}</h3>
        <CloseButton closeStyle={styles.Close} onClick={() => toggleHistory(false)} />
      </div>
      <div id="history-content" className={styles.Content}>
        <TableListEditableNew
          list={campaignHistory.statuses}
          columns={columns}
          loading={!campaignHistory.statuses}
        />
      </div>
    </section>
  );
};

const mapStateToProps = (state) => ({
  campaignHistory: getCampaignHistory(state),
  timeZone: getSettingsInfo(state)?.timezone,
  campaignInfo: getCampaignInfo(state),
  subCampaignInfo: getSubCampaignInfo(state),
});

const actionCreators = {
  toggleHistory: toggleCampaignHistory,
  setHistory: setCampaignHistory,
};

export default connect(mapStateToProps, actionCreators)(CampaignHistory);
